import * as THREE from 'three';
import { readBuild } from './build-info.js';
import { withCaptureSession } from './capture-session.js';
import { exportGLTFBase64, downloadGLTF } from './exporters.js';
import { serializeObject } from './serialize-scene.js';
import {
  applyVertexPositions,
  bevelEdge as bevelEdgeTopology,
  buildGeometryFromTopology,
  captureTopology,
  normalizeShading,
  isDefaultShading,
  SHADING_MODES,
  cloneTopology,
  computeGeometricAdjacency,
  describeTopology,
  extrudeFace as extrudeFaceTopology,
  extrudeRegion as extrudeRegionTopology,
  faceNormal,
  finiteVec3,
  insetFace as insetFaceTopology,
  insetRegion as insetRegionTopology,
  orientFacesOutside as recalculateOutsideTopology,
  weldVertices as weldTopology,
  parseTopology,
  scaleFace as scaleFaceTopology,
  scaleRegion as scaleRegionTopology,
  subdivideFace as subdivideFaceTopology,
  subdivideRegion as subdivideRegionTopology,
} from './mesh-topology.js';
import { loopCut as loopCutTopology } from './mesh-topology/operators/loop-cut.js';
import { buildViewCamera, parseViewSpec, resolveViewSpec } from './view-spec.js';
import { resolveSelector } from './mesh-selectors.js';
import { buildSculptGraph } from './mesh-topology/sculpt-neighbors.js';
import { meshDiagnostics as meshDiagnosticsTopology } from './mesh-topology/diagnostics.js';
import { assertSourceRenderable, moveCorners as moveCornersTopology } from './mesh-topology/operators/move-corners.js';
import { cornerFingerprint, objectHash, rawTopologyFingerprint } from './mesh-topology/fingerprints.js';

// Whether a command is remembered across reloads is declared in
// src/session-rules.js — add new read-only commands to READ_ONLY there.

/**
 * The command table — the entire API surface the bridge exposes.
 *
 * Every handler takes a single `args` object and returns something JSON
 * serialisable (or a Promise of it). Whatever is returned comes straight back
 * out of the CLI, so returning the new id from the add-commands means you can
 * address an object on the very next call.
 *
 * To add a command: write a function here, add it to the returned object. That
 * is the whole process — the bridge picks it up with no registration step, and
 * an unknown name already replies with the list of valid ones.
 */
export function createCommands({ viewport, registry }) {
  const {
    scene,
    camera,
    renderer,
    content,
    controls,
    render,
    pauseRendering = () => {},
    resumeRendering = () => {},
  } = viewport;

  // -- argument coercion ----------------------------------------------------
  // Deliberately forgiving: [x,y,z], {x,y,z}, or a bare number all work, so
  // hand-typed CLI args don't need to be precise about shape.

  function vec3(input, fallback = [0, 0, 0]) {
    if (input == null) return new THREE.Vector3(...fallback);
    if (typeof input === 'number') return new THREE.Vector3(input, input, input);
    if (Array.isArray(input)) {
      const [x = 0, y = 0, z = 0] = input;
      return new THREE.Vector3(num(x), num(y), num(z));
    }
    if (typeof input === 'object') {
      return new THREE.Vector3(
        num(input.x ?? fallback[0]),
        num(input.y ?? fallback[1]),
        num(input.z ?? fallback[2]),
      );
    }
    throw new Error(`expected a vector, got ${JSON.stringify(input)}`);
  }

  function num(value, name = 'value') {
    const n = Number(value);
    if (!Number.isFinite(n)) throw new Error(`${name} must be a finite number, got ${JSON.stringify(value)}`);
    return n;
  }

  /**
   * Shared `distance`-vs-`translation` resolution for `extrudeFace` and
   * `extrudeRegion` — exactly one of the two, never both, matching every
   * other extrude validation in this file. `commandName` is only used to
   * label the error, so both commands report their own name rather than a
   * shared helper's.
   */
  function resolveExtrudeDisplacement(topology, faceId, args, commandName) {
    const hasTranslation = args.translation != null;
    const hasDistance = args.distance != null;
    if (hasTranslation === hasDistance) {
      throw new Error(`${commandName} requires exactly one of "distance" or "translation"`);
    }
    return hasTranslation
      ? finiteVec3(args.translation, 'translation')
      : faceNormal(topology, faceId).map((n) => n * num(args.distance, 'distance'));
  }

  function material(args = {}) {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(args.color ?? 0x9aa4b2),
      roughness: args.roughness ?? 0.55,
      metalness: args.metalness ?? 0.05,
    });
  }

  /**
   * Shrink a mesh object's `params` down to a digest before it goes back to a
   * caller as a command result.
   *
   * `registry.describe()` is generic and always returns the object's full
   * `anvilParams` — correct and required for replay (`creationEntries` below,
   * and `serialize-scene.js`, both need the complete topology to reconstruct
   * the object). But every command's *response* to whoever called it is a
   * different concern: nobody asking to move, duplicate, or convert an object
   * needs its entire vertex/face topology echoed back, and for a dense mesh
   * that's a large, easily-forgotten-about payload. `getMeshData` is the one
   * place that opts into the full topology; every other response goes through
   * here so a future new command can't reintroduce the leak by using
   * `registry.describe()` directly.
   *
   * No fallback for a `mesh`-typed object missing `params.topology`: every
   * path that produces `anvilType: 'mesh'` (`addMesh`, `toEditableMesh`) also
   * sets `params.topology` in the same step, so that combination should be
   * unreachable. If it ever happens anyway, `describeTopology` throwing on
   * the missing topology is the right outcome — surfacing a broken invariant
   * immediately beats silently falling through to `described` unchanged,
   * which would quietly return whatever `params` the object actually has
   * (self-defeating for the one thing this function exists to guarantee).
   */
  function toResponse(described) {
    if (described.type !== 'mesh') return described;
    return { ...described, params: describeTopology(described.params.topology) };
  }

  /** Shared tail for the add-* commands. */
  function place(mesh, args, type, params) {
    mesh.position.copy(vec3(args.position));
    if (args.rotation) applyRotationDeg(mesh, vec3(args.rotation), false);
    if (args.scale != null) mesh.scale.copy(vec3(args.scale, [1, 1, 1]));
    if (args.name) mesh.name = String(args.name);
    // An explicit id is how the bridge reproduces the original id when it
    // rebuilds a scene from its log. Also usable by hand if you want to choose
    // the name yourself: addBox '{"id":"pillar"}'.
    const parent = args.parent ? registry.require(args.parent) : content;
    const id = registry.add(mesh, type, params, args.id ? String(args.id) : null, parent);
    return toResponse(registry.describe(registry.require(id)));
  }

  // -- copying --------------------------------------------------------------

  /**
   * Clone an object, including its geometry and materials.
   *
   * Three's `clone()` shares both by reference. That would be a memory win and
   * a correctness disaster here: editing the copy's colour would change the
   * original, and deleting either one disposes geometry the other is still
   * drawing with.
   */
  function deepCopy(source) {
    const copy = source.clone(true);
    copy.traverse((node) => {
      if (node.geometry) node.geometry = node.geometry.clone();
      if (Array.isArray(node.material)) node.material = node.material.map((m) => m.clone());
      else if (node.material) node.material = node.material.clone();
    });
    return copy;
  }

  /** Give a cloned subtree fresh ids and register every node in it. */
  function registerSubtree(node, parent) {
    const type = node.userData.anvilType;
    const params = node.userData.anvilParams ?? {};
    const kids = node.children.filter((child) => child.userData?.anvilId);

    // clone() copied the source's identity along with everything else. Clear it
    // so the registry mints a new id and the name follows it.
    delete node.userData.anvilId;
    node.name = '';

    const id = registry.add(node, type, params, null, parent);
    for (const kid of kids) registerSubtree(kid, node);
    return id;
  }

  /**
   * The commands that would rebuild this object from nothing, parents first.
   *
   * This is what lets a copy be remembered without depending on its source. Log
   * `duplicate box_1` and deleting box_1 later prunes its creating entry, so the
   * replay tries to copy something that no longer exists — while the copy is
   * still sitting in the scene. Recording the copy as its own creation removes
   * the dependency entirely, and leaves the compaction rules untouched.
   *
   * The command name is derived from the type and the arguments come straight
   * back out of `anvilParams`, so adding a new primitive needs no changes here.
   */
  function creationEntries(object, out = []) {
    const described = registry.describe(object);
    const common = {
      id: described.id,
      name: described.name,
      parent: described.parent,
      position: described.position,
      rotation: described.rotationDeg,
      scale: described.scale,
    };

    if (described.type === 'group') {
      // Members are recorded separately, each naming this group as its parent,
      // so the group itself is created empty at its exact transform.
      out.push({ cmd: 'group', args: { ...common, ids: [] }, id: described.id });
    } else {
      const cmd = `add${described.type[0].toUpperCase()}${described.type.slice(1)}`;
      out.push({
        cmd,
        args: {
          ...described.params,
          ...common,
          color: described.color,
          roughness: described.roughness,
          metalness: described.metalness,
        },
        id: described.id,
      });
    }

    for (const child of object.children) {
      if (child.userData?.anvilId) creationEntries(child, out);
    }
    return out;
  }

  function applyRotationDeg(object, degrees, relative) {
    const target = new THREE.Euler(
      THREE.MathUtils.degToRad(degrees.x),
      THREE.MathUtils.degToRad(degrees.y),
      THREE.MathUtils.degToRad(degrees.z),
    );
    if (relative) {
      object.rotation.x += target.x;
      object.rotation.y += target.y;
      object.rotation.z += target.z;
    } else {
      object.rotation.copy(target);
    }
  }

  // Camera angles are degrees, matching the command API's object rotations.
  // Azimuth is measured around world +Y with 0 at +Z and +90 at +X.
  const CAMERA_DIRECTIONS = {
    iso: [1, 0.8, 1],
    front: [0, 0, 1],
    back: [0, 0, -1],
    left: [-1, 0, 0],
    right: [1, 0, 0],
    top: [0, 1, 0],
    bottom: [0, -1, 0],
  };

  function strictFiniteNumber(value, name) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new Error(`${name} must be a finite number, got ${JSON.stringify(value)}`);
    }
    return value;
  }

  function cameraVector(input, name) {
    if (Array.isArray(input)) {
      if (input.length !== 3) throw new Error(`${name} must be a complete [x,y,z] vector`);
      return new THREE.Vector3(
        strictFiniteNumber(input[0], `${name}.x`),
        strictFiniteNumber(input[1], `${name}.y`),
        strictFiniteNumber(input[2], `${name}.z`),
      );
    }
    if (input && typeof input === 'object') {
      const keys = Object.keys(input).sort();
      if (keys.length !== 3 || keys[0] !== 'x' || keys[1] !== 'y' || keys[2] !== 'z') {
        throw new Error(`${name} must be a complete {x,y,z} vector`);
      }
      return new THREE.Vector3(
        strictFiniteNumber(input.x, `${name}.x`),
        strictFiniteNumber(input.y, `${name}.y`),
        strictFiniteNumber(input.z, `${name}.z`),
      );
    }
    throw new Error(`${name} must be a complete [x,y,z] or {x,y,z} vector`);
  }

  function cameraArgs(args, command, allowed) {
    if (!args || typeof args !== 'object' || Array.isArray(args)) {
      throw new Error(`${command} arguments must be an object`);
    }
    const unknown = Object.keys(args).find((key) => !allowed.includes(key));
    if (unknown) throw new Error(`${command} does not support "${unknown}"`);
    return args;
  }

  function cameraState(position = camera.position, target = controls.target) {
    const offset = position.clone().sub(target);
    const distance = offset.length();
    if (!(distance > 0)) throw new Error('camera position and target must not be the same');
    return {
      position: position.toArray(),
      target: target.toArray(),
      distance,
      azimuth: THREE.MathUtils.radToDeg(Math.atan2(offset.x, offset.z)),
      elevation: THREE.MathUtils.radToDeg(Math.atan2(offset.y, Math.hypot(offset.x, offset.z))),
    };
  }

  /**
   * The single command-side camera mutation path. Keeping target, OrbitControls,
   * matrices, and the existing persisted-camera `end` event together prevents
   * each camera command from acquiring subtly different behavior.
   */
  function applyCameraPose(position, target) {
    cameraState(position, target); // validate the complete pose before mutation
    camera.position.copy(position);
    controls.target.copy(target);
    // Flush any in-progress OrbitControls damping/pan state, then restore the
    // exact requested pose. Otherwise a command issued just after a manual
    // gesture could inherit its residual delta and cease to be deterministic.
    const damping = controls.enableDamping;
    controls.enableDamping = false;
    controls.update();
    controls.enableDamping = damping;
    camera.position.copy(position);
    controls.target.copy(target);
    camera.lookAt(target);
    camera.updateMatrixWorld(true);
    controls.dispatchEvent({ type: 'end' });
    return cameraState();
  }

  function inspectRound(values, places = 4) {
    const factor = 10 ** places;
    return values.map((value) => {
      const rounded = Math.round(value * factor) / factor;
      return Object.is(rounded, -0) ? 0 : rounded;
    });
  }

  function describeBounds(box) {
    if (box.isEmpty()) return null;
    return {
      min: inspectRound(box.min.toArray()),
      max: inspectRound(box.max.toArray()),
      size: inspectRound(box.getSize(new THREE.Vector3()).toArray()),
      center: inspectRound(box.getCenter(new THREE.Vector3()).toArray()),
    };
  }

  function renderedLocalBounds(root) {
    const bounds = new THREE.Box3();
    const inverseRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();
    const localMatrix = new THREE.Matrix4();
    const corner = new THREE.Vector3();
    root.traverse((node) => {
      const geometry = node.geometry;
      if (!geometry?.attributes?.position) return;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const geometryBounds = geometry.boundingBox;
      if (!geometryBounds || geometryBounds.isEmpty()) return;
      localMatrix.multiplyMatrices(inverseRoot, node.matrixWorld);
      for (const x of [geometryBounds.min.x, geometryBounds.max.x]) {
        for (const y of [geometryBounds.min.y, geometryBounds.max.y]) {
          for (const z of [geometryBounds.min.z, geometryBounds.max.z]) {
            bounds.expandByPoint(corner.set(x, y, z).applyMatrix4(localMatrix));
          }
        }
      }
    });
    return bounds;
  }

  function topologyLocalBounds(object) {
    if (object.userData.anvilType !== 'mesh') return null;
    const topology = object.userData.anvilParams.topology;
    const bounds = new THREE.Box3();
    for (const vertex of topology.vertices) {
      bounds.expandByPoint(new THREE.Vector3(...vertex.position));
    }
    return describeBounds(bounds);
  }

  function transformEntry(object) {
    const described = registry.describe(object);
    return {
      id: described.id,
      position: described.position,
      rotationDeg: described.rotationDeg,
      scale: described.scale,
    };
  }

  function captureArgs(args) {
    cameraArgs(args, 'captureView', ['viewSpec', 'isolate', 'helpers', 'background']);
    if (args.helpers !== undefined && typeof args.helpers !== 'boolean') {
      throw new Error('captureView helpers must be a boolean');
    }
    const background = args.background ?? '#8a8a8a';
    if (typeof background !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(background)) {
      throw new Error('captureView background must be a six-digit hex colour');
    }

    let isolate = null;
    if (args.isolate !== undefined) {
      if (!args.isolate || typeof args.isolate !== 'object' || Array.isArray(args.isolate)) {
        throw new Error('captureView isolate must be an object');
      }
      const unknown = Object.keys(args.isolate).find((key) => !['roots', 'includeDescendants'].includes(key));
      if (unknown) throw new Error(`captureView isolate does not support "${unknown}"`);
      if (!Array.isArray(args.isolate.roots)
        || args.isolate.roots.some((id) => typeof id !== 'string' || id.length === 0)) {
        throw new Error('captureView isolate.roots must be an array of registered ids');
      }
      if (args.isolate.includeDescendants !== undefined
        && typeof args.isolate.includeDescendants !== 'boolean') {
        throw new Error('captureView isolate.includeDescendants must be a boolean');
      }
      isolate = {
        roots: [...args.isolate.roots],
        includeDescendants: args.isolate.includeDescendants ?? true,
      };
    }
    return { background, helpers: args.helpers ?? false, isolate };
  }

  function captureIsolation(isolate) {
    const objects = registry.list().map(({ id }) => registry.require(id));
    if (!isolate) return { objects, keepIds: null, isolatedIds: [], hiddenCount: 0 };

    const keepIds = new Set();
    const isolatedIds = new Set();
    for (const id of isolate.roots) {
      const root = registry.require(id);
      keepIds.add(id);
      isolatedIds.add(id);
      if (isolate.includeDescendants) {
        for (const descendantId of registry.descendantIds(root)) {
          keepIds.add(descendantId);
          isolatedIds.add(descendantId);
        }
      }
      for (let ancestor = root.parent; ancestor?.userData?.anvilId; ancestor = ancestor.parent) {
        keepIds.add(ancestor.userData.anvilId);
      }
    }
    return {
      objects,
      keepIds,
      isolatedIds: objects
        .map((object) => object.userData.anvilId)
        .filter((id) => isolatedIds.has(id)),
      hiddenCount: objects.filter((object) => !keepIds.has(object.userData.anvilId)).length,
    };
  }


  /**
   * The descriptor `objectHash` hashes: the snapshot payload plus `parent`.
   *
   * Built here rather than inside the fingerprint module so that module stays
   * free of a three dependency, and so every caller hashes the same shape —
   * `resolveSelection`, `meshDiagnostics`, `getObjectSnapshot` and
   * `restoreObjectSnapshot` must agree byte for byte or their guards compare
   * different things.
   */
  /**
   * Rebuild an existing mesh's geometry, taking shading from the object rather
   * than from the caller.
   *
   * Every topology-editing command rebuilds geometry, and there are fifteen of
   * them. If shading were a parameter each of those sites had to remember to
   * pass, forgetting one would silently reset that object to flat on the next
   * edit — a defect invisible until someone noticed their smooth mesh had gone
   * faceted after an unrelated extrude. Reading it from the object makes that
   * unforgettable by construction.
   */
  function meshGeometry(object, topology) {
    return buildGeometryFromTopology(topology, object.userData.anvilParams?.shading);
  }

  function objectHashDescriptor(object, topology) {
    const described = registry.describe(object);
    return {
      topology,
      // Read the transform off the OBJECT, not off `described`.
      // `registry.describe()` rounds position/rotation/scale to 4 decimals to
      // keep `listObjects` readable — right for display, wrong for a hash. A
      // nudge smaller than 1e-4 would leave objectHash unchanged, and
      // `restoreObjectSnapshot` would then silently overwrite an intervening
      // edit it could not see. `objectHash` canonicalises these at 12 places
      // itself, matching the precision `serialize-scene.js`'s `exactTransform`
      // persists, so the hash is as precise as the state it guards.
      transform: {
        position: object.position.toArray(),
        rotationDeg: [
          THREE.MathUtils.radToDeg(object.rotation.x),
          THREE.MathUtils.radToDeg(object.rotation.y),
          THREE.MathUtils.radToDeg(object.rotation.z),
        ],
        scale: object.scale.toArray(),
      },
      parent: described.parent,
      name: described.name,
      material: {
        color: described.color,
        roughness: described.roughness,
        metalness: described.metalness,
      },
      // Shading is part of "the state you last saw". A restore that silently
      // dropped it would put the topology back and leave the object rendering
      // differently, which is exactly what objectHash exists to prevent.
      // Normalized, so an object that has never been given shading hashes the
      // same as one explicitly set to flat.
      shading: normalizeShading(object.userData.anvilParams?.shading),
    };
  }

  // -- commands -------------------------------------------------------------

  const commands = {
    /** Cheapest possible liveness check. */
    ping() {
      return { pong: true, objects: registry.size, time: new Date().toISOString() };
    },

    listObjects() {
      return { count: registry.size, objects: registry.list().map(toResponse) };
    },

    inspectObject(args = {}) {
      cameraArgs(args, 'inspectObject', ['id', 'includeChain']);
      if (typeof args.id !== 'string' || args.id.length === 0) {
        throw new Error('inspectObject requires a non-empty string "id"');
      }
      if (args.includeChain !== undefined && typeof args.includeChain !== 'boolean') {
        throw new Error('inspectObject includeChain must be a boolean');
      }

      const object = registry.require(args.id);
      const described = registry.describe(object);
      object.updateWorldMatrix(true, false);

      const worldBounds = new THREE.Box3().setFromObject(object);
      const localBounds = renderedLocalBounds(object);
      const worldPosition = new THREE.Vector3();
      const worldQuaternion = new THREE.Quaternion();
      const worldScale = new THREE.Vector3();
      object.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);
      const worldRotation = new THREE.Euler().setFromQuaternion(worldQuaternion, 'XYZ');

      const chain = [];
      if (args.includeChain) {
        for (let ancestor = object.parent; ancestor?.userData?.anvilId; ancestor = ancestor.parent) {
          chain.unshift(transformEntry(ancestor));
        }
      }

      return {
        id: described.id,
        parent: described.parent,
        chain,
        // Reported explicitly, and normalized, so a caller never has to infer
        // "no shading key" as flat the way the stored representation does.
        // Meshes only — a primitive has no topology to interpret.
        ...(object.userData.anvilType === 'mesh'
          ? { shading: normalizeShading(object.userData.anvilParams?.shading) }
          : {}),
        local: {
          position: described.position,
          rotationDeg: described.rotationDeg,
          scale: described.scale,
        },
        world: {
          position: inspectRound(worldPosition.toArray()),
          rotationDeg: inspectRound([
            THREE.MathUtils.radToDeg(worldRotation.x),
            THREE.MathUtils.radToDeg(worldRotation.y),
            THREE.MathUtils.radToDeg(worldRotation.z),
          ]),
          scale: inspectRound(worldScale.toArray()),
          matrix: inspectRound(object.matrixWorld.toArray()),
        },
        bounds: {
          local: describeBounds(localBounds),
          world: describeBounds(worldBounds),
          topologyLocal: topologyLocalBounds(object),
        },
      };
    },

    captureView(args = {}) {
      const options = captureArgs(args);
      const viewSpec = parseViewSpec(args.viewSpec);
      const resolved = resolveViewSpec(viewSpec, { registry });
      const isolation = captureIsolation(options.isolate);
      const cameraForCapture = buildViewCamera(resolved);

      return withCaptureSession(viewport, {
        width: resolved.width,
        height: resolved.height,
        background: options.background,
        helpers: options.helpers,
        objects: isolation.objects,
        keepIds: isolation.keepIds,
      }, () => {
        renderer.render(scene, cameraForCapture);
        const dataUrl = renderer.domElement.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1] ?? '';
        const padding = base64.endsWith('==') ? 2 : (base64.endsWith('=') ? 1 : 0);
        const byteLength = Math.floor(base64.length * 3 / 4) - padding;
        const drawingBuffer = renderer.getDrawingBufferSize(new THREE.Vector2());
        return {
          projection: resolved.projection,
          width: drawingBuffer.x,
          height: drawingBuffer.y,
          pixelRatio: 1,
          camera: {
            space: viewSpec.space,
            ...(viewSpec.anchorId ? { anchorId: viewSpec.anchorId } : {}),
            resolvedWorld: { position: resolved.position, target: resolved.target },
          },
          isolatedIds: isolation.isolatedIds,
          hiddenCount: isolation.hiddenCount,
          build: readBuild(),
          format: 'png',
          byteLength,
          base64,
        };
      });
    },

    addBox(args = {}) {
      const size = vec3(args.size ?? 1, [1, 1, 1]);
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        material(args),
      );
      return place(mesh, args, 'box', { size: size.toArray() });
    },

    addSphere(args = {}) {
      const radius = num(args.radius ?? 0.5, 'radius');
      const widthSegments = Math.max(3, Math.floor(args.widthSegments ?? 32));
      const heightSegments = Math.max(2, Math.floor(args.heightSegments ?? 16));
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(radius, widthSegments, heightSegments),
        material(args),
      );
      return place(mesh, args, 'sphere', { radius, widthSegments, heightSegments });
    },

    addCylinder(args = {}) {
      const radiusTop = num(args.radiusTop ?? args.radius ?? 0.5, 'radiusTop');
      const radiusBottom = num(args.radiusBottom ?? args.radius ?? 0.5, 'radiusBottom');
      const height = num(args.height ?? 1, 'height');
      const radialSegments = Math.max(3, Math.floor(args.radialSegments ?? 32));
      const mesh = new THREE.Mesh(
        new THREE.CylinderGeometry(radiusTop, radiusBottom, height, radialSegments),
        material(args),
      );
      return place(mesh, args, 'cylinder', { radiusTop, radiusBottom, height, radialSegments });
    },

    /**
     * Create a mesh object directly from canonical topology.
     *
     * This is what replay calls for any `mesh`-typed object (see
     * PRIMITIVE_CREATION_COMMANDS in serialize-scene.js) — `toEditableMesh`
     * below produces the same shape by capturing an existing primitive
     * instead of taking topology as an argument.
     */
    addMesh(args = {}) {
      const topology = parseTopology(args.topology);
      const shading = normalizeShading(args.shading);
      const mesh = new THREE.Mesh(buildGeometryFromTopology(topology, shading), material(args));
      // Default shading is not stored, so a mesh that never asks for anything
      // serializes exactly as it did before Milestone D.
      const params = isDefaultShading(shading) ? { topology } : { topology, shading };
      return place(mesh, args, 'mesh', params);
    },

    /**
     * Convert an existing primitive to an editable mesh, capturing its
     * current geometry as canonical topology.
     *
     * One-way and explicit: once converted, the object's parametric fields
     * (radius, segments, ...) no longer apply, and there is no "convert
     * back." Undo reverses it like any other command; there is no separate
     * toggle. Primitives that are never converted keep working exactly as
     * before — this does not touch addBox/addSphere/addCylinder.
     */
    toEditableMesh(args = {}) {
      if (!args.id) throw new Error('toEditableMesh requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType === 'mesh') {
        throw new Error(`'${args.id}' is already an editable mesh`);
      }
      if (!object.isMesh || !object.geometry) {
        throw new Error(`'${args.id}' has no geometry to convert to an editable mesh`);
      }
      const topology = captureTopology(object.geometry);
      object.userData.anvilType = 'mesh';
      object.userData.anvilParams = { topology };
      return toResponse(registry.describe(object));
    },

    /** Full vertex/face topology for one mesh object. Not part of listObjects — ask for it explicitly. */
    getMeshData(args = {}) {
      if (!args.id) throw new Error('getMeshData requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      return { id: args.id, topology: cloneTopology(object.userData.anvilParams.topology) };
    },

    /** Recalculate consistent outward winding for every closed topology component. */
    /**
     * Set how a mesh's topology is interpreted by the renderer.
     *
     * Shape and shading are deliberately separate: this changes no vertex, no
     * face, and no id, so `rawTopologyFingerprint` and `cornerFingerprint` are
     * untouched and any selection a caller is holding stays valid. It does
     * change `objectHash`, because the object no longer looks the way it did.
     *
     * Scoped to editable meshes. Primitives render through THREE's own
     * geometry and already carry their own normals; giving them a second,
     * conflicting shading model is not this milestone's job.
     */
    setShading(args = {}) {
      if (!args.id) throw new Error('setShading requires an "id"');
      if (args.mode === undefined) {
        throw new Error(`setShading requires a "mode" — one of ${SHADING_MODES.join(', ')}`);
      }
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') {
        throw new Error(`'${args.id}' is not an editable mesh — shading applies to meshes`);
      }

      // Validate before touching anything: normalizeShading throws on a bad
      // mode or angle, and it must do so while the object is still untouched.
      const shading = normalizeShading({ mode: args.mode, angleDeg: args.angleDeg });

      const params = object.userData.anvilParams;
      const previousShading = params.shading;
      const previousGeometry = object.geometry;
      // Absent means flat, so flat is stored as absence. One state, one
      // representation — otherwise two identical-looking objects could hash
      // differently depending on how they got to flat.
      if (isDefaultShading(shading)) delete params.shading;
      else params.shading = shading;

      try {
        object.geometry = meshGeometry(object, params.topology);
        previousGeometry.dispose();
      } catch (error) {
        if (previousShading === undefined) delete params.shading;
        else params.shading = previousShading;
        object.geometry = previousGeometry;
        throw error;
      }

      return {
        ...toResponse(registry.describe(object)),
        shading,
        renderVertexCount: object.geometry.attributes.position.count,
        vertexCount: params.topology.vertices.length,
        faceCount: params.topology.faces.length,
      };
    },

    /**
     * Merge coincident vertices, giving a primitive-derived mesh the shared
     * connectivity its topology never had.
     *
     * `toEditableMesh` captures a THREE geometry's render buffer faithfully,
     * duplicates and all — a box arrives as 24 vertices at 8 positions. That is
     * the right thing for a conversion to do (§E4 of the Phase 2 plan: silent
     * welding would destroy intentional seams), but it leaves a mesh where no
     * edge is shared, so `recalculateOutside` refuses it and smooth shading has
     * nothing to average across. This is the explicit repair.
     *
     * Conservative on purpose. It merges what is already coincident; collapsing
     * merely-nearby geometry is a different intent and a different command.
     */
    weldVertices(args = {}) {
      if (!args.id) throw new Error('weldVertices requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);

      const params = object.userData.anvilParams;
      // The whole candidate is built and validated before anything is
      // committed, so a refusal leaves the object byte-identical.
      const outcome = weldTopology(params.topology, {
        ...(args.tolerance === undefined ? {} : { tolerance: num(args.tolerance, 'tolerance') }),
      });
      const candidate = parseTopology(outcome.topology);

      if (!outcome.changed) {
        return {
          ...toResponse(registry.describe(object)),
          status: 'unchanged',
          mergedVertexCount: 0,
          removedFaceCount: 0,
          before: outcome.before,
          after: outcome.after,
        };
      }

      const previousTopology = params.topology;
      const previousGeometry = object.geometry;
      try {
        params.topology = candidate;
        object.geometry = meshGeometry(object, candidate);
        previousGeometry.dispose();
      } catch (error) {
        params.topology = previousTopology;
        object.geometry = previousGeometry;
        throw error;
      }

      return {
        ...toResponse(registry.describe(object)),
        status: 'welded',
        mergedVertexCount: outcome.mergedVertexCount,
        removedFaceCount: outcome.removedFaceCount,
        before: outcome.before,
        after: outcome.after,
        renderVertexCount: object.geometry.attributes.position.count,
      };
    },

    recalculateOutside(args = {}) {
      if (!args.id) throw new Error('recalculateOutside requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      const outcome = recalculateOutsideTopology(object.userData.anvilParams.topology);
      if (outcome.flippedFaceIds.length > 0) {
        object.geometry.dispose();
        object.geometry = meshGeometry(object, object.userData.anvilParams.topology);
      }
      return {
        ...toResponse(registry.describe(object)),
        status: outcome.flippedFaceIds.length > 0 ? 'recalculated' : 'unchanged',
        componentCount: outcome.componentCount,
        flippedFaceIds: outcome.flippedFaceIds,
      };
    },

    /**
     * Set one or more vertices to absolute positions, addressed by their
     * stable topology id (e.g. "v3"), never by array index — indices shift
     * under future topology-changing operations, ids do not.
     *
     * `changes` is `[[vertexId, position], ...]`. All-or-nothing: an unknown
     * id fails the whole call before any position is touched.
     */
    setVertexPositions(args = {}) {
      if (!args.id) throw new Error('setVertexPositions requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (!Array.isArray(args.changes) || args.changes.length === 0) {
        throw new Error('setVertexPositions requires a non-empty "changes" array');
      }
      const changes = args.changes.map((entry, index) => {
        if (!Array.isArray(entry) || entry.length !== 2 || typeof entry[0] !== 'string') {
          throw new Error(`changes[${index}] must be [vertexId, position]`);
        }
        // finiteVec3, not vec3(): vec3()'s forgiving shapes (bare-number
        // scalar fill, {x,y,z}, partial-array zero-fill) exist for hand-typed
        // CLI convenience on whole-object transforms. A single named vertex
        // has no such reasonable default — [5] or {x:5} is almost certainly a
        // caller mistake, not an intentional partial move, so reject anything
        // that isn't a complete, explicit [x, y, z] rather than silently
        // filling in zeros.
        return [entry[0], finiteVec3(entry[1], `changes[${index}] position`)];
      });

      const topology = object.userData.anvilParams.topology;
      applyVertexPositions(topology, changes);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return toResponse(registry.describe(object));
    },

    /**
     * Extrude exactly the one named triangular face along its own outward
     * normal (`distance`) or an explicit vector (`translation`) — exactly
     * one of the two, never both. The source face is removed and replaced
     * with a translated cap plus 6 side-wall triangles stitching it back to
     * the untouched base ring (see `extrudeFace` in mesh-topology.js for the
     * exact topology it produces).
     *
     * A visible box face is captured as *two* coplanar triangles sharing a
     * diagonal (see mesh-topology.js's module doc), so extruding only one of
     * them via this command leaves the other flat, producing a pinched wedge
     * rather than a clean rectangular bump. That's an intentional, precise
     * single-triangle operation, useful when a caller genuinely wants exactly
     * one triangle moved — for "extrude the visible face a human just
     * clicked," use `extrudeRegion` instead, which discovers and extrudes
     * that triangle's whole connected coplanar region as one unit.
     */
    extrudeFace(args = {}) {
      if (!args.id) throw new Error('extrudeFace requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('extrudeFace requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const displacement = resolveExtrudeDisplacement(topology, args.faceId, args, 'extrudeFace');
      const outcome = extrudeFaceTopology(topology, args.faceId, displacement);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return { ...toResponse(registry.describe(object)), capFaceId: outcome.capFaceId, newFaceIds: outcome.newFaceIds };
    },

    /**
     * Extrude the connected coplanar region containing the named face —
     * "the visible face a human clicked," not just the one triangle the
     * click happened to hit. For a normal box face (two triangles sharing a
     * diagonal), this extrudes both together as one clean rectangular bump,
     * with no wall generated along their shared internal edge. Reduces to
     * exactly `extrudeFace`'s single-triangle behavior when the named face
     * has no coplanar, connected neighbor. See `coplanarRegion` and
     * `extrudeRegion` in mesh-topology.js for the discovery/extrusion
     * algorithm — this command does not duplicate it.
     *
     * `capFaceId` in the response is one deterministic face from the new cap
     * region (not the whole region) — chaining another extrude from it
     * rediscovers the complete new region on its own, so a caller (UI or
     * agent) never needs to track more than one id across a chained series
     * of extrudes.
     */
    extrudeRegion(args = {}) {
      if (!args.id) throw new Error('extrudeRegion requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('extrudeRegion requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const displacement = resolveExtrudeDisplacement(topology, args.faceId, args, 'extrudeRegion');
      const outcome = extrudeRegionTopology(topology, args.faceId, displacement);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        capFaceId: outcome.capFaceId,
        capFaceIds: outcome.capFaceIds,
        newFaceIds: outcome.newFaceIds,
      };
    },

    /**
     * Inset exactly the one named triangular face by `amount` — a fixed
     * perpendicular offset, never a vector (inset has no meaningful
     * "direction" input the way extrude's `translation` does, since the
     * offset is computed per-boundary-vertex, inward within the face's own
     * plane). The source face is removed and replaced with a smaller,
     * same-facing inner cap plus a surrounding ring of border triangles — see
     * `insetFace` in mesh-topology.js for the exact topology it produces.
     *
     * Same two-triangles-per-visible-face caveat as `extrudeFace`: insetting
     * only one triangle of a box quad's pair leaves the other untouched,
     * producing a lopsided ring rather than a clean picture-frame border. For
     * "inset the visible face a human just clicked," use `insetRegion`
     * instead.
     */
    insetFace(args = {}) {
      if (!args.id) throw new Error('insetFace requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('insetFace requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const amount = num(args.amount, 'amount');
      const outcome = insetFaceTopology(topology, args.faceId, amount);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return { ...toResponse(registry.describe(object)), capFaceId: outcome.capFaceId, newFaceIds: outcome.newFaceIds };
    },

    /**
     * Inset the connected coplanar region containing the named face — "the
     * visible face a human clicked," not just the one triangle the click
     * happened to hit. For a normal box face (two triangles sharing a
     * diagonal), this insets both together as one clean rectangular border,
     * with no ring generated along their shared internal edge. Reduces to
     * exactly `insetFace`'s single-triangle behavior when the named face has
     * no coplanar, connected neighbor. See `coplanarRegion` and `insetRegion`
     * in mesh-topology.js for the discovery/inset algorithm — this command
     * does not duplicate it.
     *
     * `capFaceId` in the response is one deterministic face from the new
     * inner region (not the whole region). Unlike `extrudeRegion`, chaining
     * another region operation from it does *not* reliably reselect just the
     * shrunk inner region: inset deliberately leaves the ring and inner cap
     * coplanar (no new hard edge, so the result reads as one flat surface,
     * not a stepped border), which means `coplanarRegion` from any face in
     * the result rediscovers the *entire* original flat area, ring included.
     * A second `insetRegion` call from that rediscovered region then fails
     * with a clear "vertex interior to its own boundary" error (the
     * ring's inner corners aren't on the merged region's own outer boundary)
     * rather than producing invalid geometry — insetting the same visible
     * face twice needs a fresh `distance`-based `extrudeRegion` (which
     * introduces the hard edge inset itself doesn't) before another inset
     * can target just the smaller face again.
     */
    insetRegion(args = {}) {
      if (!args.id) throw new Error('insetRegion requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('insetRegion requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const amount = num(args.amount, 'amount');
      const outcome = insetRegionTopology(topology, args.faceId, amount);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        capFaceId: outcome.capFaceId,
        capFaceIds: outcome.capFaceIds,
        newFaceIds: outcome.newFaceIds,
      };
    },

    /**
     * Scale exactly the one named triangle by `factor`, uniformly within its
     * own plane, around the arithmetic centroid of its own 3 vertices —
     * never moved along its normal. Unlike extrude/inset, this mints no new
     * vertices or faces: it only rewrites existing vertex positions (the
     * same underlying primitive `setVertexPositions` uses), so every
     * topology id is fully preserved and the same `faceId` remains valid for
     * a repeated call. See `scaleFace` in mesh-topology.js for the exact
     * validation (positive, finite, not exactly 1) and the degenerate/
     * self-intersection guard.
     *
     * Same two-triangles-per-visible-face caveat as `extrudeFace`/`insetFace`:
     * scaling only one triangle of a box quad's pair moves half the visible
     * face and leaves the other in place. For "scale the visible face a
     * human just clicked," use `scaleRegion` instead.
     */
    scaleFace(args = {}) {
      if (!args.id) throw new Error('scaleFace requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('scaleFace requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const factor = num(args.factor, 'factor');
      const outcome = scaleFaceTopology(topology, args.faceId, factor);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        faceId: outcome.faceId,
        scaledVertexIds: outcome.scaledVertexIds,
        center: outcome.center,
      };
    },

    /**
     * Scale the connected coplanar region containing the named face — "the
     * visible face a human clicked," not just the one triangle the click
     * happened to hit. For a normal box face (two triangles sharing a
     * diagonal), this scales both together as one clean resized face, around
     * their shared centroid. Reduces to exactly `scaleFace`'s single-triangle
     * behavior when the named face has no coplanar, connected neighbor. See
     * `coplanarRegion` and `scaleRegion` in mesh-topology.js for the
     * discovery/scale algorithm — this command does not duplicate it.
     *
     * Scaling a face that is attached to already-existing geometry sharing
     * its vertex ids (an extruded cap's side walls, an inset's border ring)
     * tapers that attached geometry along with it, since those faces
     * reference the very same vertex ids being moved — this is the intended
     * way to turn an extrude into a tapered/pyramid shape. See
     * `scaleRegion`'s own doc comment in mesh-topology.js for the one
     * documented limitation: scaling an inset's inner face via `scaleRegion`
     * (not `scaleFace`) rediscovers the whole flattened inset area through
     * `coplanarRegion`, the same way a second `insetRegion` on that face
     * does.
     */
    scaleRegion(args = {}) {
      if (!args.id) throw new Error('scaleRegion requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('scaleRegion requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const factor = num(args.factor, 'factor');
      const outcome = scaleRegionTopology(topology, args.faceId, factor);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        faceIds: outcome.faceIds,
        scaledVertexIds: outcome.scaledVertexIds,
        center: outcome.center,
      };
    },

    /**
     * Subdivide exactly the one named triangular face into 4, via the
     * standard edge-midpoint split — the source face is removed and
     * replaced with 3 corner triangles plus 1 center triangle, sharing 3
     * new midpoint vertices (see `subdivideFace` in mesh-topology.js for
     * the exact construction). Unlike extrude/inset/scale, this takes no
     * magnitude argument at all: there is no distance/amount/factor to
     * choose, only which face — a caller wanting more density chains
     * another subdivide off the returned `centerFaceId`.
     *
     * Same two-triangles-per-visible-face caveat as `extrudeFace`/`insetFace`/
     * `scaleFace`: subdividing only one triangle of a box quad's pair
     * leaves the other at its original density. That's an intentional,
     * precise single-triangle operation — for "subdivide the visible face a
     * human just clicked," use `subdivideRegion` instead.
     */
    subdivideFace(args = {}) {
      if (!args.id) throw new Error('subdivideFace requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('subdivideFace requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const outcome = subdivideFaceTopology(topology, args.faceId);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        removedFaceId: outcome.removedFaceId,
        removedFaceIds: outcome.removedFaceIds,
        newFaceIds: outcome.newFaceIds,
        centerFaceId: outcome.centerFaceId,
        conformedFaceIds: outcome.conformedFaceIds,
      };
    },

    /**
     * Subdivide the connected coplanar region containing the named face —
     * "the visible face a human clicked," not just the one triangle the
     * click happened to hit. For a normal box face (two triangles sharing a
     * diagonal), this subdivides both together as one watertight patch,
     * sharing exactly one midpoint vertex on their shared diagonal rather
     * than tearing into two independently-subdivided halves. Reduces to
     * exactly `subdivideFace`'s single-triangle behavior when the named
     * face has no coplanar, connected neighbor. See `coplanarRegion` and
     * `subdivideRegion` in mesh-topology.js for the discovery/subdivision
     * algorithm — this command does not duplicate it.
     *
     * `centerFaceId` in the response is one deterministic face from the new
     * center-triangle set (not the whole result) — the natural "select this
     * afterward" analog to extrude/inset's `capFaceId`. Unlike extrude,
     * chaining another subdivide from it does not subdivide just that one
     * triangle again: subdivision introduces no new hard edge, so
     * `coplanarRegion` from any resulting face rediscovers every new face
     * from this whole call. See `subdivideRegion`'s own doc comment in
     * mesh-topology.js for the details.
     */
    subdivideRegion(args = {}) {
      if (!args.id) throw new Error('subdivideRegion requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.faceId !== 'string' || args.faceId.length === 0) {
        throw new Error('subdivideRegion requires a "faceId"');
      }

      const topology = object.userData.anvilParams.topology;
      const outcome = subdivideRegionTopology(topology, args.faceId);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        removedFaceIds: outcome.removedFaceIds,
        newFaceIds: outcome.newFaceIds,
        centerFaceId: outcome.centerFaceId,
        centerFaceIds: outcome.centerFaceIds,
        conformedFaceIds: outcome.conformedFaceIds,
      };
    },

    /**
     * Bevel (chamfer) exactly one *geometric* hard edge — MVP1: a convex
     * edge whose two endpoint geometric corners each have exactly 3
     * incident visible regions and 3 incident geometric edges. See
     * `computeBevelEdge` in mesh-topology.js for the full algorithm and
     * eligibility rules.
     *
     * `edgeId` must be an `edges[].id` from a *current* `getMeshAdjacency`
     * call against this same object — never a raw topology edge id (the
     * `v3_v7`-shaped ids `edgesOfTopology`/current Edge-mode picking use)
     * and never an internal triangulation diagonal. Both are rejected with
     * a specific error rather than only "not found," and so is a
     * genuinely stale geometric id left over from before some other edit —
     * this derived id space is not persisted and must be recomputed (via
     * `getMeshAdjacency`) after any topology-changing command, including a
     * prior `bevelEdge` itself.
     *
     * The two flanking regions keep their existing face ids (only the
     * beveled corner's own vertex references change); the two endpoint cap
     * regions are retired and rebuilt. Does not return canonical topology —
     * `getMeshData` remains the one command that opts into that.
     */
    bevelEdge(args = {}) {
      if (!args.id) throw new Error('bevelEdge requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.edgeId !== 'string' || args.edgeId.length === 0) {
        throw new Error('bevelEdge requires an "edgeId" (a geometric edge id from getMeshAdjacency)');
      }
      const width = num(args.width, 'width');

      const topology = object.userData.anvilParams.topology;
      const outcome = bevelEdgeTopology(topology, args.edgeId, width);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        bevelFaceIds: outcome.bevelFaceIds,
        newVertexIds: outcome.newVertexIds,
        newFaceIds: outcome.newFaceIds,
        removedFaceIds: outcome.removedFaceIds,
        affectedFaceIds: outcome.affectedFaceIds,
      };
    },

    /**
     * Apply the fixed-MVP Loop Cut to the ordered logical quad traversal
     * starting at one current geometric edge. The operator owns all topology
     * discovery, validation, deterministic id allocation, and atomic commit;
     * this command only supplies the editable mesh and rebuilds its geometry.
     */
    loopCut(args = {}) {
      if (!args.id) throw new Error('loopCut requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (typeof args.edgeId !== 'string' || args.edgeId.length === 0) {
        throw new Error('loopCut requires an "edgeId" (a geometric edge id from getMeshAdjacency)');
      }

      const topology = object.userData.anvilParams.topology;
      const outcome = loopCutTopology(topology, args.edgeId);
      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      return {
        ...toResponse(registry.describe(object)),
        status: outcome.status,
        startEdgeId: outcome.startEdgeId,
        cellIds: outcome.cellIds,
        crossedEdgeIds: outcome.crossedEdgeIds,
        newVertexIds: outcome.newVertexIds,
        newFaceIds: outcome.newFaceIds,
        removedFaceIds: outcome.removedFaceIds,
        cutTopologyEdgeIds: outcome.cutTopologyEdgeIds,
      };
    },

    /**
     * Read-only derived geometric adjacency for one editable mesh — the
     * regions/corners/edges/diagonals `computeGeometricAdjacency` computes,
     * cross-referenced by id. This is how an agent (or, later, the UI)
     * discovers a valid `edgeId` for `bevelEdge` without duplicating that
     * discovery logic: raw canonical topology has no notion of a single
     * user-meaningful hard edge (see mesh-topology.js's own module comment
     * on why render-vertex granularity splits every hard edge into two
     * vertex-disjoint halves).
     *
     * Recomputed fresh on every call, exactly like `getMeshData` reads
     * current topology fresh — nothing here is cached or persisted, so the
     * result is only valid against the topology snapshot it was computed
     * from. Read-only: see session-rules.js's `READ_ONLY` set.
     */
    /**
     * Resolve a spatial predicate to concrete corner ids, plus the guards a
     * caller needs to reuse them safely.
     *
     * Read-only and a pure function of current topology, so it is safe on the
     * single arbitrary viewer the bridge routes reads to: every replica would
     * answer identically.
     *
     * Returns all three fingerprints because they answer different questions
     * and a caller threading a selection through several of its own edits needs
     * all of them — raw identity to know the ids still mean these elements,
     * corner identity to know the groups still hold, and the object hash to
     * detect an intervening edit that moved nothing else. `revision` is NOT
     * returned here: the browser does not hold the authoritative revision, and
     * the bridge envelope supplies it.
     */
    async resolveSelection(args = {}) {
      cameraArgs(args, 'resolveSelection', ['id', 'selector', 'granularity']);
      if (typeof args.id !== 'string' || args.id.length === 0) {
        throw new Error('resolveSelection requires a non-empty string "id"');
      }
      const granularity = args.granularity ?? 'corner';
      if (granularity !== 'corner' && granularity !== 'vertex') {
        throw new Error(`resolveSelection granularity must be "corner" or "vertex", got ${JSON.stringify(args.granularity)}`);
      }
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      const topology = object.userData.anvilParams.topology;

      const { corners } = computeGeometricAdjacency(topology);
      const graph = buildSculptGraph(topology, corners);
      object.updateWorldMatrix(true, false);

      const cornerIds = resolveSelector(args.selector, {
        corners,
        graph,
        matrixWorld: object.matrixWorld,
        resolveScreenSpec: (rawViewSpec) => {
          const resolved = resolveViewSpec(parseViewSpec(rawViewSpec), { registry });
          return { camera: buildViewCamera(resolved), width: resolved.width, height: resolved.height };
        },
      });

      const byId = new Map(corners.map((corner) => [corner.id, corner]));
      const vertexIds = cornerIds.flatMap((cornerId) => byId.get(cornerId).vertexIds).sort();
      const bounds = new THREE.Box3();
      for (const cornerId of cornerIds) bounds.expandByPoint(new THREE.Vector3(...byId.get(cornerId).position));

      const corner = await cornerFingerprint(topology);
      return {
        id: args.id,
        granularity,
        cornerIds,
        vertexIds,
        count: granularity === 'vertex' ? vertexIds.length : cornerIds.length,
        bounds: cornerIds.length === 0 ? null : {
          min: bounds.min.toArray(),
          max: bounds.max.toArray(),
          size: bounds.getSize(new THREE.Vector3()).toArray(),
          center: bounds.getCenter(new THREE.Vector3()).toArray(),
        },
        rawTopologyFingerprint: await rawTopologyFingerprint(topology),
        cornerFingerprint: corner.value,
        ...(corner.error ? { cornerFingerprintError: corner.error } : {}),
        objectHash: await objectHash(objectHashDescriptor(object, topology)),
      };
    },

    /**
     * Move geometric corners by a relative displacement, optionally mirrored
     * and optionally faded across a neighbourhood.
     *
     * RELATIVE ONLY in Phase 1. Absolute targets have undefined semantics for
     * several seeds sharing one target, for absolute combined with falloff, and
     * for a mirrored absolute target; `setVertexPositions` remains available for
     * explicitly calculated coordinates.
     *
     * `baseRevision` is validated and stripped by the bridge before relay, so it
     * never arrives here — receiving it means the guard was bypassed, and the
     * unknown-argument rejection below is the right response.
     *
     * VALIDATION ORDER IS LOAD-BEARING. Source renderability is checked before
     * adjacency, because `computeGeometricAdjacency` itself throws on a mesh
     * containing a degenerate face; a check after it could never be reached, and
     * the caller would get adjacency's error rather than one naming the faces.
     */
    async moveCorners(args = {}) {
      cameraArgs(args, 'moveCorners', [
        'id', 'cornerIds', 'selector', 'delta', 'space', 'mirror', 'falloff',
        'expectedRawTopologyFingerprint', 'expectedCornerFingerprint',
      ]);
      if (typeof args.id !== 'string' || args.id.length === 0) {
        throw new Error('moveCorners requires a non-empty string "id"');
      }
      const hasIds = Array.isArray(args.cornerIds);
      const hasSelector = args.selector !== undefined;
      if (hasIds === hasSelector) {
        throw new Error('moveCorners requires exactly one of "cornerIds" or "selector"');
      }
      const delta = finiteVec3(args.delta, 'delta');
      const space = args.space ?? 'local';
      if (space !== 'local' && space !== 'world') {
        throw new Error(`moveCorners space must be "local" or "world", got ${JSON.stringify(args.space)}`);
      }
      for (const field of ['expectedRawTopologyFingerprint', 'expectedCornerFingerprint']) {
        if (typeof args[field] !== 'string' || args[field].length === 0) {
          throw new Error(`moveCorners requires a "${field}" — resolve the selection first`);
        }
      }

      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      const topology = object.userData.anvilParams.topology;

      // 1. Refuse an already-broken mesh, before anything derived is computed.
      assertSourceRenderable(topology, 'moveCorners');

      // 2. Derive, then check both guards.
      const { corners } = computeGeometricAdjacency(topology);
      const graph = buildSculptGraph(topology, corners);
      const actualRaw = await rawTopologyFingerprint(topology);
      if (actualRaw !== args.expectedRawTopologyFingerprint) {
        const error = new Error(
          'moveCorners: the mesh topology changed since the selection was resolved; re-resolve it',
        );
        error.code = 'STALE_RAW_TOPOLOGY_FINGERPRINT';
        error.expected = args.expectedRawTopologyFingerprint;
        error.actual = actualRaw;
        throw error;
      }
      const actualCorner = await cornerFingerprint(topology);
      if (actualCorner.value !== args.expectedCornerFingerprint) {
        const error = new Error(
          'moveCorners: corner grouping changed since the selection was resolved; re-resolve it',
        );
        error.code = 'STALE_CORNER_FINGERPRINT';
        error.expected = args.expectedCornerFingerprint;
        error.actual = actualCorner.value;
        throw error;
      }

      const cornerIds = hasIds ? args.cornerIds : resolveSelector(args.selector, {
        corners,
        graph,
        matrixWorld: (object.updateWorldMatrix(true, false), object.matrixWorld),
        resolveScreenSpec: (rawViewSpec) => {
          const resolved = resolveViewSpec(parseViewSpec(rawViewSpec), { registry });
          return { camera: buildViewCamera(resolved), width: resolved.width, height: resolved.height };
        },
      });

      // A world-space delta is a DIRECTION, so it converts through the world
      // matrix's rotation/scale only — never its translation.
      let localDelta = delta;
      if (space === 'world') {
        object.updateWorldMatrix(true, false);
        const basis = new THREE.Matrix3().setFromMatrix4(object.matrixWorld).invert();
        localDelta = new THREE.Vector3(...delta).applyMatrix3(basis).toArray();
      }

      // 3-5. Candidate build, validation and commit all happen in the operator,
      //      so a rejected move leaves canonical topology byte identical.
      const outcome = moveCornersTopology(topology, {
        corners,
        graph,
        cornerIds,
        delta: localDelta,
        mirror: args.mirror ?? null,
        falloff: args.falloff ?? null,
      });

      object.geometry.dispose();
      object.geometry = meshGeometry(object, topology);

      const described = toResponse(registry.describe(object));
      const afterCorner = await cornerFingerprint(topology);
      return {
        id: args.id,
        ...outcome,
        vertexCount: described.params.vertexCount,
        faceCount: described.params.faceCount,
        rawTopologyFingerprint: actualRaw,
        cornerFingerprint: afterCorner.value,
        objectHash: await objectHash(objectHashDescriptor(object, topology)),
      };
    },

    /**
     * Structural health of one editable mesh, computed from RAW topology so a
     * mesh `computeGeometricAdjacency` refuses can still be diagnosed — which
     * is exactly when a diagnosis is most wanted.
     *
     * Findings are separate from the verdict. An open mesh is not a broken
     * mesh, so boundary only drives the verdict when the caller states an
     * expectation through `expectedClosed`; `verdictContributors` names what
     * actually drove it.
     */
    async meshDiagnostics(args = {}) {
      cameraArgs(args, 'meshDiagnostics', ['id', 'expectedClosed', 'sampleLimit']);
      if (typeof args.id !== 'string' || args.id.length === 0) {
        throw new Error('meshDiagnostics requires a non-empty string "id"');
      }
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      const topology = object.userData.anvilParams.topology;

      const report = await meshDiagnosticsTopology(topology, {
        expectedClosed: args.expectedClosed ?? null,
        ...(args.sampleLimit === undefined ? {} : { sampleLimit: args.sampleLimit }),
      });
      return {
        id: args.id,
        ...report,
        objectHash: await objectHash(objectHashDescriptor(object, topology)),
      };
    },

    /**
     * Capture one object's restorable state as a portable VALUE.
     *
     * A value rather than a server-side checkpoint, deliberately. The caller
     * holds it, so nothing new is persisted, the hosted SQLite write budget is
     * untouched, and there is no browser-local state a second viewer could be
     * missing — the same statelessness rule the rest of Phase 1 follows.
     *
     * `byteLength` is reported so a caller learns the payload size before
     * discovering the 8 MB request-body limit on the way back in.
     *
     * PARENT IS NOT CAPTURED and is not restorable in Phase 1. It is still part
     * of `objectHash`, so a reparent by anyone else causes a restore to be
     * rejected rather than silently reverting geometry into a hierarchy that
     * has moved underneath it.
     */
    async getObjectSnapshot(args = {}) {
      cameraArgs(args, 'getObjectSnapshot', ['id']);
      if (typeof args.id !== 'string' || args.id.length === 0) {
        throw new Error('getObjectSnapshot requires a non-empty string "id"');
      }
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') {
        throw new Error(`'${args.id}' is not an editable mesh — only mesh objects can be snapshotted`);
      }
      const topology = object.userData.anvilParams.topology;
      const described = registry.describe(object);

      const snapshot = {
        schema: 1,
        type: described.type,
        topology: cloneTopology(topology),
        transform: {
          position: object.position.toArray(),
          rotationDeg: [
            THREE.MathUtils.radToDeg(object.rotation.x),
            THREE.MathUtils.radToDeg(object.rotation.y),
            THREE.MathUtils.radToDeg(object.rotation.z),
          ],
          scale: object.scale.toArray(),
        },
        name: described.name,
        material: {
          color: described.color,
          roughness: described.roughness,
          metalness: described.metalness,
        },
        // Captured so a restore puts back the object as seen, not merely its
        // shape. A snapshot written before Milestone D has no shading key; the
        // restore reads that as flat, which is what those objects were.
        shading: normalizeShading(object.userData.anvilParams?.shading),
      };

      return {
        id: args.id,
        snapshot,
        objectHash: await objectHash(objectHashDescriptor(object, topology)),
        // TextEncoder, not Buffer: this file runs in the BROWSER, where Buffer
        // does not exist. Node-only globals pass every unit test here and then
        // throw in a real viewer, which is exactly how this one was found.
        byteLength: new TextEncoder().encode(JSON.stringify(snapshot)).length,
      };
    },

    /**
     * Put a captured snapshot back, but only onto the state the caller last saw.
     *
     * `expectedCurrentObjectHash` is the hash of the CURRENT state, not the
     * snapshot's own. That distinction is the whole safety property: a caller
     * snapshots at H0, makes several edits of its own reaching Hn, and restores
     * quoting Hn. Passing H0 would mean the caller has not looked since taking
     * the snapshot, and a restore on that basis would overwrite everything in
     * between without noticing — so it is refused.
     *
     * `baseRevision` is validated and stripped by the bridge before relay and
     * never arrives here; the browser performs no revision check of its own.
     * What it does check is the object hash, which answers a different
     * question — stale addressing and replica divergence, not authority.
     */
    async restoreObjectSnapshot(args = {}) {
      cameraArgs(args, 'restoreObjectSnapshot', ['id', 'snapshot', 'expectedCurrentObjectHash']);
      if (typeof args.id !== 'string' || args.id.length === 0) {
        throw new Error('restoreObjectSnapshot requires a non-empty string "id"');
      }
      if (typeof args.expectedCurrentObjectHash !== 'string' || args.expectedCurrentObjectHash.length === 0) {
        throw new Error(
          'restoreObjectSnapshot requires "expectedCurrentObjectHash" — the CURRENT hash of the '
          + 'object, which getObjectSnapshot or meshDiagnostics reports',
        );
      }
      const snapshot = args.snapshot;
      if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
        throw new Error('restoreObjectSnapshot requires a "snapshot" object');
      }
      if (snapshot.schema !== 1) {
        throw new Error(`restoreObjectSnapshot supports snapshot schema 1, got ${JSON.stringify(snapshot.schema)}`);
      }
      if (!snapshot.transform || typeof snapshot.transform !== 'object') {
        throw new Error('restoreObjectSnapshot requires a snapshot.transform object');
      }

      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      if (snapshot.type !== undefined && snapshot.type !== object.userData.anvilType) {
        throw new Error(
          `restoreObjectSnapshot: this snapshot is of a '${snapshot.type}', but '${args.id}' is a `
          + `'${object.userData.anvilType}'`,
        );
      }
      const current = object.userData.anvilParams.topology;

      // Validate the COMPLETE payload before mutating anything, the same rule
      // setMaterial already follows. Without it a malformed transform is caught
      // only once it has been applied: the topology is swapped, the bad value
      // lands on the object, and the failure surfaces from the trailing hash —
      // leaving position or scale holding strings, which poisons every later
      // hash, serialization and render of that object. A snapshot is untrusted
      // input; a hand-edited .anvil/snapshots file is enough to reach this.
      const restoredPosition = finiteVec3(snapshot.transform.position, 'snapshot.transform.position');
      const restoredRotation = finiteVec3(snapshot.transform.rotationDeg ?? [0, 0, 0], 'snapshot.transform.rotationDeg');
      const restoredScale = finiteVec3(snapshot.transform.scale ?? [1, 1, 1], 'snapshot.transform.scale');
      if (snapshot.name !== undefined && typeof snapshot.name !== 'string') {
        throw new Error('restoreObjectSnapshot: snapshot.name must be a string');
      }
      // Throws on a malformed mode or angle, and must do so here — while the
      // object is still untouched — rather than part-way through the apply.
      // A snapshot predating Milestone D has no shading key and normalizes to
      // flat, which is the state those objects were captured in.
      const restoredShading = normalizeShading(snapshot.shading);
      const restoredMaterial = snapshot.material ?? null;
      if (restoredMaterial !== null) {
        if (typeof restoredMaterial !== 'object' || Array.isArray(restoredMaterial)) {
          throw new Error('restoreObjectSnapshot: snapshot.material must be an object');
        }
        // THREE.Color.set() only warns on an unparseable string and leaves the
        // colour untouched, so a bad value would otherwise report a successful
        // restore that did not restore the colour. Validated against the shape
        // getObjectSnapshot actually writes.
        if (restoredMaterial.color != null && !/^#[0-9a-fA-F]{6}$/.test(String(restoredMaterial.color))) {
          throw new Error(
            `restoreObjectSnapshot: snapshot.material.color must be a #rrggbb string, got ${JSON.stringify(restoredMaterial.color)}`,
          );
        }
        for (const field of ['roughness', 'metalness']) {
          const value = restoredMaterial[field];
          if (value != null && (typeof value !== 'number' || !Number.isFinite(value))) {
            throw new Error(`restoreObjectSnapshot: snapshot.material.${field} must be a finite number`);
          }
        }
      }

      // Validate the incoming topology through the SAME primitive addMesh uses,
      // never a weaker parser — a snapshot is untrusted input like any other.
      const restoredTopology = parseTopology(snapshot.topology);

      const actualHash = await objectHash(objectHashDescriptor(object, current));
      if (actualHash !== args.expectedCurrentObjectHash) {
        const error = new Error(
          'restoreObjectSnapshot: the object changed since the hash you quoted; '
          + 're-read its current objectHash and decide again',
        );
        error.code = 'STALE_OBJECT_HASH';
        error.expected = args.expectedCurrentObjectHash;
        error.actual = actualHash;
        throw error;
      }

      // Everything validated. Swap, and put the previous state back if the
      // geometry rebuild fails, so a half-restored object cannot survive.
      // Everything validated. Record the whole prior state, then apply — and on
      // any failure put all of it back, not just the topology. A half-restored
      // object must not survive.
      const previousTopology = cloneTopology(current);
      const previousTransform = {
        position: object.position.toArray(),
        rotation: [object.rotation.x, object.rotation.y, object.rotation.z],
        scale: object.scale.toArray(),
      };
      const previousName = object.name;
      const previousShading = object.userData.anvilParams.shading;
      const previousMaterial = object.material
        ? {
          color: object.material.color?.getHex(),
          roughness: object.material.roughness,
          metalness: object.material.metalness,
        }
        : null;

      try {
        object.userData.anvilParams.topology = restoredTopology;
        // Shading first: meshGeometry reads it off the object, so setting it
        // after the rebuild would restore the shape and render it wrong.
        if (isDefaultShading(restoredShading)) delete object.userData.anvilParams.shading;
        else object.userData.anvilParams.shading = restoredShading;
        object.geometry.dispose();
        object.geometry = meshGeometry(object, restoredTopology);

        object.position.set(restoredPosition[0], restoredPosition[1], restoredPosition[2]);
        applyRotationDeg(object, vec3(restoredRotation), false);
        object.scale.set(restoredScale[0], restoredScale[1], restoredScale[2]);
        if (typeof snapshot.name === 'string') object.name = snapshot.name;
        if (restoredMaterial && object.material) {
          if (restoredMaterial.color != null) object.material.color.set(restoredMaterial.color);
          if (restoredMaterial.roughness != null) object.material.roughness = restoredMaterial.roughness;
          if (restoredMaterial.metalness != null) object.material.metalness = restoredMaterial.metalness;
          object.material.needsUpdate = true;
        }
      } catch (error) {
        object.userData.anvilParams.topology = previousTopology;
        if (previousShading === undefined) delete object.userData.anvilParams.shading;
        else object.userData.anvilParams.shading = previousShading;
        object.geometry = meshGeometry(object, previousTopology);
        object.position.fromArray(previousTransform.position);
        object.rotation.set(...previousTransform.rotation);
        object.scale.fromArray(previousTransform.scale);
        object.name = previousName;
        if (previousMaterial && object.material) {
          if (previousMaterial.color !== undefined) object.material.color.setHex(previousMaterial.color);
          object.material.roughness = previousMaterial.roughness;
          object.material.metalness = previousMaterial.metalness;
          object.material.needsUpdate = true;
        }
        throw error;
      }

      const corner = await cornerFingerprint(restoredTopology);
      return {
        id: args.id,
        restoredFields: ['topology', 'transform', 'name', 'material'],
        objectHash: await objectHash(objectHashDescriptor(object, restoredTopology)),
        rawTopologyFingerprint: await rawTopologyFingerprint(restoredTopology),
        cornerFingerprint: corner.value,
        ...(corner.error ? { cornerFingerprintError: corner.error } : {}),
        vertexCount: restoredTopology.vertices.length,
        faceCount: restoredTopology.faces.length,
      };
    },

    getMeshAdjacency(args = {}) {
      if (!args.id) throw new Error('getMeshAdjacency requires an "id"');
      const object = registry.require(args.id);
      if (object.userData.anvilType !== 'mesh') throw new Error(`'${args.id}' is not an editable mesh`);
      const topology = object.userData.anvilParams.topology;
      return { id: args.id, ...computeGeometricAdjacency(topology) };
    },

    deleteObject(args = {}) {
      if (!args.id) throw new Error('deleteObject requires an "id"');
      const ancestorGroupIds = [];
      let ancestor = registry.require(args.id).parent;
      while (ancestor) {
        if (ancestor.userData.anvilType === 'group' && ancestor.userData.anvilId) {
          ancestorGroupIds.push(ancestor.userData.anvilId);
        }
        ancestor = ancestor.parent;
      }
      // Deleting a group takes its members with it, so this is a list.
      const deleted = registry.remove(args.id);
      // Legacy logs did not pin a group's creation pivot. Return the live pose
      // of every surviving ancestor so deletion compaction can migrate those
      // entries before their member list changes.
      const groups = ancestorGroupIds
        .map((id) => registry.get(id))
        .filter(Boolean)
        .map((group) => toResponse(registry.describe(group)));
      const canonicalRoot = registry.get(ancestorGroupIds.at(-1));
      const record = canonicalRoot ? creationEntries(canonicalRoot) : null;
      return { deleted, remaining: registry.size, groups, ...(record ? { record } : {}) };
    },

    /**
     * Parent objects into a group so they move as one.
     *
     * Two shapes. Normally you pass `ids` and the group's origin lands on the
     * centre of their combined bounds, so rotating it spins the assembly about
     * its middle instead of about the world origin. Pass an explicit transform
     * instead (which is what a rebuild does) and the group is created exactly
     * there, empty, for members to be added into by `parent`.
     */
    group(args = {}) {
      const ids = Array.isArray(args.ids) ? args.ids : [];
      const members = ids.map((id) => registry.require(id));
      const group = new THREE.Group();

      const posed = args.position != null || args.rotation != null || args.scale != null;
      if (posed) {
        group.position.copy(vec3(args.position));
        if (args.rotation) applyRotationDeg(group, vec3(args.rotation), false);
        if (args.scale != null) group.scale.copy(vec3(args.scale, [1, 1, 1]));
      } else if (members.length > 0) {
        // Same stale-ancestor gap focusObject was fixed for: expandByObject
        // walks a member's own subtree down but never its ancestors up.
        const bounds = new THREE.Box3();
        for (const member of members) {
          member.updateWorldMatrix(true, false);
          bounds.expandByObject(member);
        }
        group.position.copy(bounds.getCenter(new THREE.Vector3()));
      }

      if (args.name) group.name = String(args.name);
      const parent = args.parent ? registry.require(args.parent) : content;
      const id = registry.add(group, 'group', {}, args.id ? String(args.id) : null, parent);

      // reparent() uses attach(), so members keep their place on screen.
      for (const member of members) registry.reparent(member, group);

      return toResponse(registry.describe(group));
    },

    /** Dissolve a group, leaving its members where they are. */
    ungroup(args = {}) {
      const group = registry.require(args.id);
      if (group.userData.anvilType !== 'group') {
        throw new Error(`'${args.id}' is not a group`);
      }
      const target = group.parent ?? content;
      const freed = registry.childIdsOf(group);
      // Collect ids first: reparenting mutates the children array underneath us.
      for (const childId of freed) registry.reparent(registry.require(childId), target);
      registry.remove(args.id);
      return { ungrouped: args.id, freed };
    },

    /**
     * Copy an object, optionally several times along an offset.
     *
     * `count` turns this into an array tool: each copy is offset by a further
     * multiple, so `{offset:[2,0,0], count:3}` gives a row of three.
     */
    duplicate(args = {}) {
      const source = registry.require(args.id);
      const count = Math.max(1, Math.floor(num(args.count ?? 1, 'count')));
      const offset = vec3(args.offset ?? [0, 0, 0]);
      const parent = source.parent ?? content;
      const created = [];

      for (let step = 1; step <= count; step++) {
        const copy = deepCopy(source);
        copy.position.add(offset.clone().multiplyScalar(step));
        registerSubtree(copy, parent);
        created.push(copy);
      }

      return {
        created: created.map((copy) => copy.userData.anvilId),
        objects: created.map((copy) => toResponse(registry.describe(copy))),
        // Remember each copy as its own creation rather than as "a copy of
        // that" — see serializeObject().
        record: created.flatMap((copy) => serializeObject(registry, copy)),
      };
    },

    /** Mirror a copy across an axis plane. */
    mirror(args = {}) {
      const source = registry.require(args.id);
      const axis = String(args.axis ?? 'x').toLowerCase();
      const index = { x: 0, y: 1, z: 2 }[axis];
      if (index === undefined) throw new Error(`axis must be x, y or z, got '${args.axis}'`);
      const pivot = args.pivot != null ? num(args.pivot, 'pivot') : 0;
      const parent = source.parent ?? content;

      parent.updateWorldMatrix(true, false);
      source.updateWorldMatrix(true, false);
      const worldPosition = new THREE.Vector3();
      const worldQuaternion = new THREE.Quaternion();
      const worldScale = new THREE.Vector3();
      source.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);
      worldPosition.setComponent(index, 2 * pivot - worldPosition.getComponent(index));

      // Reflect orientation in the same world-space plane as position. Applying
      // this to the local quaternion is only correct for an untransformed parent.
      const parts = [worldQuaternion.x, worldQuaternion.y, worldQuaternion.z]
        .map((value, component) => (component === index ? value : -value));
      worldQuaternion.set(parts[0], parts[1], parts[2], worldQuaternion.w);

      const copy = deepCopy(source);
      registerSubtree(copy, parent);
      const mirroredWorld = new THREE.Matrix4().compose(worldPosition, worldQuaternion, worldScale);
      const local = new THREE.Matrix4().copy(parent.matrixWorld).invert().multiply(mirroredWorld);
      local.decompose(copy.position, copy.quaternion, copy.scale);
      copy.updateMatrixWorld(true);

      return {
        ...toResponse(registry.describe(copy)),
        record: serializeObject(registry, copy),
      };
    },

    moveObject(args = {}) {
      const object = registry.require(args.id);
      const delta = vec3(args.position);
      if (args.relative) object.position.add(delta);
      else object.position.copy(delta);
      return toResponse(registry.describe(object));
    },

    /** Rotation in degrees. Pass relative:true to add to the current rotation. */
    rotateObject(args = {}) {
      const object = registry.require(args.id);
      applyRotationDeg(object, vec3(args.rotation), Boolean(args.relative));
      return toResponse(registry.describe(object));
    },

    scaleObject(args = {}) {
      const object = registry.require(args.id);
      const scale = vec3(args.scale ?? 1, [1, 1, 1]);
      if (args.relative) object.scale.multiply(scale);
      else object.scale.copy(scale);
      return toResponse(registry.describe(object));
    },

    setMaterial(args = {}) {
      const object = registry.require(args.id);
      const mat = object.material;
      if (!mat) throw new Error(`object '${args.id}' has no material`);
      // Validate the complete patch before changing anything. Otherwise a bad
      // later field can leave a colour change behind even though the command
      // reports failure and is not committed by the bridge.
      const color = args.color != null ? new THREE.Color().set(args.color) : null;
      const roughness = args.roughness != null ? num(args.roughness, 'roughness') : null;
      const metalness = args.metalness != null ? num(args.metalness, 'metalness') : null;
      if (color) mat.color.copy(color);
      if (roughness != null) mat.roughness = roughness;
      if (metalness != null) mat.metalness = metalness;
      mat.needsUpdate = true;
      return toResponse(registry.describe(object));
    },

    clearScene() {
      const removed = registry.clear();
      return { removed, count: removed.length };
    },

    /** Current world-space Y-up camera pose and its derived spherical values. */
    getCamera(args = {}) {
      cameraArgs(args, 'getCamera', []);
      camera.updateMatrixWorld(true);
      return cameraState();
    },

    /** Set an absolute camera position and/or target. */
    setCamera(args = {}) {
      cameraArgs(args, 'setCamera', ['position', 'target']);
      const hasPosition = Object.hasOwn(args, 'position');
      const hasTarget = Object.hasOwn(args, 'target');
      if (!hasPosition && !hasTarget) {
        throw new Error('setCamera requires at least one of "position" or "target"');
      }

      // Parse the complete requested pose before touching either live vector.
      const position = hasPosition ? cameraVector(args.position, 'position') : camera.position.clone();
      const target = hasTarget ? cameraVector(args.target, 'target') : controls.target.clone();
      cameraState(position, target);
      return applyCameraPose(position, target);
    },

    /**
     * Set an absolute world-space Y-up orbit pose. Omitted spherical fields
     * preserve their current derived values; changing only target translates
     * the camera with it instead of changing the current orbit.
     */
    orbitCamera(args = {}) {
      cameraArgs(args, 'orbitCamera', ['target', 'azimuth', 'elevation', 'distance']);
      const current = cameraState();
      const target = Object.hasOwn(args, 'target')
        ? cameraVector(args.target, 'target')
        : controls.target.clone();
      const azimuth = Object.hasOwn(args, 'azimuth')
        ? strictFiniteNumber(args.azimuth, 'azimuth')
        : current.azimuth;
      const elevation = Object.hasOwn(args, 'elevation')
        ? strictFiniteNumber(args.elevation, 'elevation')
        : current.elevation;
      const distance = Object.hasOwn(args, 'distance')
        ? strictFiniteNumber(args.distance, 'distance')
        : current.distance;

      if (!(distance > 0)) throw new Error(`distance must be greater than 0, got ${distance}`);
      if (elevation < -90 || elevation > 90) {
        throw new Error(`elevation must be between -90 and 90 degrees, got ${elevation}`);
      }

      const azimuthRad = THREE.MathUtils.degToRad(azimuth);
      const elevationRad = THREE.MathUtils.degToRad(elevation);
      const horizontal = distance * Math.cos(elevationRad);
      const offset = new THREE.Vector3(
        horizontal * Math.sin(azimuthRad),
        distance * Math.sin(elevationRad),
        horizontal * Math.cos(azimuthRad),
      );
      return applyCameraPose(target.clone().add(offset), target);
    },

    /**
     * Point the camera at an object, or at the whole scene when given no id.
     *
     * The no-id form matters more than it looks: it's how you frame a shot
     * before a screenshot. Focusing one part of a larger build zooms to *that
     * part*, which is rarely what you want when checking your work — a snowman's
     * hat ends up cropped out of frame because you focused its middle snowball.
     */
    focusObject(args = {}) {
      const target = args.id ? registry.require(args.id) : content;
      if (!args.id && registry.size === 0) throw new Error('scene is empty — nothing to focus');

      // Box3 refreshes the target subtree but not stale ancestors.
      // Refresh the ancestor chain before computing world-space bounds.
      target.updateWorldMatrix(true, false);
      const box = new THREE.Box3().setFromObject(target);
      const center = box.getCenter(new THREE.Vector3());
      const padding = args.padding != null ? num(args.padding, 'padding') : 1.8;
      const distance = Math.max(box.getSize(new THREE.Vector3()).length(), 1) * padding;
      const hasDirection = Object.hasOwn(args, 'direction');
      const direction = hasDirection ? String(args.direction) : 'iso';
      const values = CAMERA_DIRECTIONS[direction];
      if (!values) {
        throw new Error(`direction must be one of ${Object.keys(CAMERA_DIRECTIONS).join(', ')}, got ${JSON.stringify(args.direction)}`);
      }
      const offset = new THREE.Vector3(...values).normalize().multiplyScalar(distance);
      applyCameraPose(center.clone().add(offset), center);
      return { focused: args.id ?? '(whole scene)', target: center.toArray() };
    },

    /**
     * Render the canvas to a PNG and hand it back base64-encoded.
     *
     * Renders synchronously first: the animation loop may not have drawn since
     * the mutation that prompted the screenshot, and preserveDrawingBuffer only
     * preserves whatever was drawn last.
     */
    screenshot(args = {}) {
      const previousSize = renderer.getSize(new THREE.Vector2());
      const previousAspect = camera.aspect;
      const width = args.width ? Math.floor(num(args.width, 'width')) : previousSize.x;
      const height = args.height ? Math.floor(num(args.height, 'height')) : previousSize.y;
      const resized = width !== previousSize.x || height !== previousSize.y;
      const displayPixelRatio = Math.min(window.devicePixelRatio, 2);
      let dataUrl;

      pauseRendering();
      try {
        renderer.setPixelRatio(1);
        if (resized) {
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
          renderer.setSize(width, height, false);
        }

        render();
        dataUrl = renderer.domElement.toDataURL('image/png');
      } finally {
        if (resized) {
          camera.aspect = previousAspect;
          camera.updateProjectionMatrix();
          renderer.setSize(previousSize.x, previousSize.y, false);
        }
        renderer.setPixelRatio(displayPixelRatio);
        resumeRendering();
        render();
      }

      const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
      return {
        format: 'png',
        width,
        height,
        pixelRatio: 1,
        byteLength: Math.floor((base64.length * 3) / 4),
        base64,
      };
    },

    /**
     * Export the content group as glTF.
     *
     * download:true triggers a browser download in the tab; otherwise the bytes
     * come back base64 so the CLI can write the file on this side.
     */
    async exportGLTF(args = {}) {
      const binary = args.binary ?? true;
      if (args.download) {
        return { downloaded: true, ...(await downloadGLTF(content, { binary, filename: args.filename })) };
      }
      return exportGLTFBase64(content, { binary });
    },

    /** Introspection, so the CLI can list what it can call. */
    help() {
      return { commands: Object.keys(commands).sort(), objects: registry.size, scene: scene.name || 'Scene' };
    },
  };

  return commands;
}
